import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Archive, ArchiveRestore, Download, ExternalLink, Inbox, Mail, RefreshCw, UserCheck2,
} from "lucide-react";
import {
  prequalificationApi,
  type PrequalBuyerDocumentPath,
  type PrequalSellerPath,
  type PrequalInternalVerification,
  type PrequalificationActivityDto,
  type PrequalificationDetailDto,
  type PrequalificationDocType,
  type PrequalificationRole,
  type PrequalificationRegenerationResultDto,
  type PrequalificationStatus,
  type PrequalScheduleFiguresDto,
} from "@/api/prequalification";
import { downloadBlob } from "@/api/reports";
import {
  useArchivePrequalification,
  useAssignPrequalification,
  useChangePrequalificationStatus,
  useConvertPrequalification,
  usePrequalificationActivity,
  usePrequalificationBuyerCrm,
  usePrequalificationDetail,
  useRegeneratePrequalificationDocuments,
  useSetInternalVerification,
  useUnarchivePrequalification,
} from "@/hooks/prequalification/usePrequalification";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useDateFormatter } from "@/utils/datetime";
import {
  Badge,
  Button,
  IconBtn,
  Input,
  Label,
  RECORD_RAIL_WIDTH,
  ResponsiveSplit,
  Textarea,
  Tooltip,
} from "@/design-system/components/ui";
import { formatCurrency, formatPhone } from "@/design-system/lib/format";
// The program terms the v7 rows are labelled with, read from the one TypeScript
// mirror of PrequalProgramTerms (pinned to the C# constants by the shared parity
// fixture) rather than typed here. Staff code may read the portal's pure modules;
// the reverse is what `portal/isolation.test.ts` forbids.
import { PREQUAL_PROGRAM_TERMS } from "@/portal/pages/prequalify/prequalSizing";
import { Modal } from "@/components/ui/Modal";
import { PartyPicker, type SelectedParty } from "@/components/parties/PartyPicker";
import { partyBasePath, type PartyType } from "@/types/parties";
import { PageHeader } from "@/components/layout/PageHeader";
import { phoneCertificationLine } from "@/lib/prequalCertification";
import { PrequalificationResendModal } from "./PrequalificationResendModal";
import { BuyerCrmLinkBack, SendToBuyerCrmModal } from "./PrequalificationBuyerCrm";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import {
  amountCell,
  internalVerificationBadge,
  outcomeLabel,
  PREQUAL_OUTCOME_BADGE,
  PREQUAL_STATUS_BADGE,
} from "./PrequalificationListPage";

// Allowed transitions — the FE mirror of PrequalificationStatuses (Converted
// is set only by the convert action, never offered directly).
const STATUS_TRANSITIONS: Record<PrequalificationStatus, PrequalificationStatus[]> = {
  New: ["Reviewed", "Disqualified"],
  Reviewed: ["Disqualified"],
  Disqualified: ["Reviewed"],
  Converted: [],
};

/** Exported for the buyer record's call-prequalification review drawer, which
 *  lists the same documents and must name them the same way. */
export const DOC_LABEL: Record<PrequalificationDocType, string> = {
  Letter: "Prequalification letter",
  DealSheet: "Deal sheet",
  Analysis: "Analysis",
};

/** The registry a submission converts into.
 *
 *  DERIVED from the role, never chosen. The server derives the same mapping from
 *  the same non-nullable column, so a picker here could only ever disagree with
 *  it — and the only thing a disagreement can produce is a buyer filed under
 *  Sellers. */
function partyTypeForRole(role: PrequalificationRole): PartyType {
  return role === "Seller" ? "Seller" : "Borrower";
}

// --- answers rendering ----------------------------------------------------

type AnswerRow = {
  label: string;
  value: string;
  badge?: "gold" | "review" | "error";
  /** A separate error badge beside the value (U5b: "Under 3 years"). */
  flag?: string;
};
type AnswerGroup = { title: string; rows: AnswerRow[] };

/** Case-insensitive lookup map over the raw answers object (the serializer
 * casing is not guaranteed — camelCase and PascalCase both parse). */
function toLowerKeyMap(obj: unknown): Map<string, unknown> {
  const m = new Map<string, unknown>();
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      m.set(k.toLowerCase(), v);
    }
  }
  return m;
}

function humanize(token: string): string {
  // prequal-v2 stores prose risk descriptions — pass those through verbatim;
  // the token → Title Case fallback covers legacy v1 rows (keys).
  if (token.includes(" ")) return token;
  const spaced = token.replace(/[-_]/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function text(map: Map<string, unknown>, key: string): string {
  const v = map.get(key.toLowerCase());
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return humanize(v);
  return String(v);
}

/** A typed name or URL exactly as the buyer wrote it (trimmed). Not `text`,
 *  whose token humanizer would turn "McKay-Hart Holdings" into
 *  "Mc Kay Hart Holdings" and a hyphenated web address into words. */
function verbatim(map: Map<string, unknown>, key: string): string {
  const v = map.get(key.toLowerCase());
  return typeof v === "string" && v.trim() !== "" ? v.trim() : "—";
}

function money(map: Map<string, unknown>, key: string): string {
  const v = map.get(key.toLowerCase());
  return typeof v === "number" ? formatCurrency(v) : "—";
}

// --- stored option tokens → the funnel's own wording ----------------------
//
// Every map below is COPIED from the funnel's option catalogs
// (`portal/pages/prequalify/prequalContent.ts` — PRICE_BANDS, RESERVES_BANDS,
// OWNERSHIP_LADDER, MANAGEMENT_LADDER, INDUSTRY_LADDER, INVOLVEMENT_OPTIONS)
// rather than imported: a staff page does not take a dependency on the portal's
// internal copy module. They therefore have to be kept in step with it — the
// cost of the isolation, and the reason `humanize` stays as the fallback.
//
// Without them the stored tokens reached staff raw: "2.5m 5m", "6mo plus",
// "Owned comparable", "Department pnl", "Direct", "Operator".

const PRICE_BAND_LABEL: Record<string, string> = {
  exploring: "Still exploring",
  "250k-500k": "$250K–$500K",
  "500k-1m": "$500K–$1M",
  "1m-2.5m": "$1M–$2.5M",
  "2.5m-5m": "$2.5M–$5M",
  "5m-10m": "$5M–$10M",
};

const RESERVES_BAND_LABEL: Record<string, string> = {
  "under-3mo": "Under 3 months of expenses",
  "3-6mo": "3–6 months",
  "6mo-plus": "6+ months",
};

const OWNERSHIP_LABEL: Record<string, string> = {
  "never-owned": "Never owned a business",
  "owned-smaller": "Owned a smaller/different business (<3 yrs)",
  "owned-comparable": "Owned a comparable business (3+ yrs)",
  "owned-multiple": "Owned multiple / built & sold",
};

const MANAGEMENT_LABEL: Record<string, string> = {
  none: "None",
  "small-team": "Led a small team (2–10)",
  "department-pnl": "Ran a department w/ P&L (10–50)",
  "executive-pnl": "Full operations / exec P&L (50+)",
};

const INDUSTRY_FIT_LABEL: Record<string, string> = {
  new: "New to this industry",
  adjacent: "Adjacent / related",
  direct: "Direct, 1–5 years",
  "direct-5plus": "Direct 5+ yrs / licensed in trade",
};

const INVOLVEMENT_LABEL: Record<string, string> = {
  operator: "I'll be the full-time, hands-on operator",
  "oversight-manager": "I'll oversee it and put a full-time manager in place",
  "hands-off": "I'd be hands-off — more of an investment",
};

/** Own-property label lookup. Every token fed to this comes out of a stored
 *  JSON blob, and a plain index would resolve inherited `Object.prototype`
 *  members instead of missing. */
function labelFor(labels: Record<string, string>, token: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(labels, token) ? labels[token] : undefined;
}

/** A stored option token rendered through its catalog label, falling back to
 *  `humanize` for an unknown or legacy value. */
function choice(
  map: Map<string, unknown>,
  key: string,
  labels: Record<string, string>,
): string {
  const v = map.get(key.toLowerCase());
  if (typeof v !== "string" || v.trim() === "") return "—";
  const token = v.trim();
  return labelFor(labels, token) ?? humanize(token);
}

/** Down-payment band ("5" | "10" | …) rendered as "5%" etc. */
function pctBand(map: Map<string, unknown>, key: string): string {
  const v = map.get(key.toLowerCase());
  if (typeof v === "number") return `${v}%`;
  if (typeof v === "string" && v.trim() !== "") return `${v.trim()}%`;
  return "—";
}

function boolText(map: Map<string, unknown>, key: string): string {
  const v = map.get(key.toLowerCase());
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return "—";
}

const GEOGRAPHY_MODE_LABEL: Record<string, string> = {
  near: "Near their city",
  region: "State / region",
  anywhere: "Anywhere in the U.S.",
};

// --- other assets toward the down payment ---------------------------------
//
// The non-cash assets a buyer said they would put toward the SAME down payment.
// "Other assets toward your down payment" is the framing everywhere a person
// reads it; the stored object is `cashEquivalents`, and that word appears in no
// label.

/** The five categories, in the order the form asks them — a fixed list so the
 *  rows keep their order and an unrecognised stored property can never mint a
 *  row of its own. Mirrors CashEquivalentFields in the server-side registry. */
const CASH_EQUIVALENT_ROWS: ReadonlyArray<readonly [string, string]> = [
  ["realestateequity", "Other assets — real estate equity"],
  ["securities", "Other assets — stock & securities"],
  ["businessequity", "Other assets — business equity"],
  ["retirementaccounts", "Other assets — retirement accounts"],
  ["otherassets", "Other assets — anything else"],
];

/** Rows for the categories the buyer actually NAMED, then their combined total
 *  and the one shared note.
 *
 *  A category the buyer did not name renders NO row. Named means a POSITIVE
 *  amount: the funnel collapses a ticked category with a blank or zero figure to
 *  absent before it posts, so zero is the product's own word for "not selected".
 *  Every surface applies that same threshold — the three documents, the broker
 *  drawer and the convert note. Every submission finalized before this
 *  section existed carries no `cashEquivalents` object at all, so the Capital
 *  group reads exactly as it did; a "—" row per category would instead claim we
 *  asked five questions and got nothing back, on every historical row.
 *
 *  The total is labelled as plain arithmetic over the rows above it, NOT as the
 *  figure the engine sized from. Sizing applies a per-category liquidity haircut
 *  (`PrequalProgramTerms.CashEquivalentHaircuts`, mirrored as
 *  `PREQUAL_CASH_EQUIVALENT_HAIRCUTS` — all 1.00 today, so the two agree
 *  exactly), and this page must not restate that number from a private sum that
 *  would quietly stop agreeing the day a haircut moves. The row that DOES make
 *  the sizing claim is the server-rendered broker drawer's, which goes through
 *  `PrequalBuyerAnswers.TotalDownPaymentCapacity` — the single reader of those
 *  haircuts. Reaching for the TS mirror here would mean rebuilding a typed
 *  answers object out of this untrusted blob, and a staff page does not import
 *  the portal funnel's internal modules (see the label maps above). */
function cashEquivalentRows(map: Map<string, unknown>): AnswerRow[] {
  const eq = toLowerKeyMap(map.get("cashequivalents"));
  const rows: AnswerRow[] = [];
  let named = 0;

  for (const [key, label] of CASH_EQUIVALENT_ROWS) {
    const v = eq.get(key);
    // Number-only, exactly like `money` above: these are serialized from
    // `decimal?`, so anything else is a shape we do not recognise. Positive,
    // not merely present: the funnel collapses a ticked-but-zero category to
    // absent before it posts, so a stored 0 can only come from a hand-built
    // payload — and the documents, the broker drawer and the convert note all
    // draw the line in the same place.
    if (typeof v !== "number" || v <= 0) continue;
    named += v;
    rows.push({ label, value: formatCurrency(v) });
  }

  if (rows.length === 0) return [];

  const cash = map.get("cashavailable");
  rows.push({
    label: "Cash + other assets",
    value: formatCurrency((typeof cash === "number" ? cash : 0) + named),
  });

  const note = eq.get("note");
  if (typeof note === "string" && note.trim() !== "") {
    rows.push({ label: "Other assets — note", value: note.trim() });
  }
  return rows;
}

/** The nested operatingGeography descriptor → "Near their city (Dallas, TX)". */
function geographyText(map: Map<string, unknown>): string {
  const geo = toLowerKeyMap(map.get("operatinggeography"));
  const mode = geo.get("mode");
  if (typeof mode !== "string" || mode.trim() === "") return "—";
  const label = GEOGRAPHY_MODE_LABEL[mode] ?? humanize(mode);
  const homeCity = geo.get("homecity");
  return typeof homeCity === "string" && homeCity.trim() !== ""
    ? `${label} (${homeCity.trim()})`
    : label;
}

// Engines whose fit-check questions are phrased so "Yes" is favorable
// ("no" = the adverse answer); anything missing/unknown is treated as the
// legacy v1 disclosure polarity ("yes" = adverse).
//
// 🔴 DERIVED, NOT LISTED (M2). It used to be a hand-kept set of version
// strings, and the v4 bump left it out: every v4 submission's knockout answers
// were badged with the legacy polarity — a favourable "yes" in the error colour
// (owner ruling Q20). The polarity inverted at v2 (PrequalProgramTerms'
// history) and every stamp since keeps it, so the rule is the stamp's own
// number: `prequal-vN` with N ≥ 2. `PrequalificationDetailPage.test.tsx`
// reads `PrequalProgramTerms.EngineVersion` from the C# source and fails when
// the current version is not classified yes-is-favourable.
const ENGINE_VERSION = /^prequal-v(\d+)$/;
export function isYesFavorableEngine(engineVersion: string | null | undefined): boolean {
  const m = ENGINE_VERSION.exec(engineVersion?.trim() ?? "");
  return m !== null && Number(m[1]) >= 2;
}

// Company & sponsor buyers (owner brief 2026-10-08) — staff labels, the TS twins
// of BuyerAcquirer.BuyerTypeLabel / EquitySourceLabel / OperatorArrangementLabel.
// Declared locally for the same reason as the label maps above. The full-time
// operator is "designated", never "named" (ruling Q4).
const BUYER_TYPE_LABEL: Record<string, string> = {
  Individual: "Individual",
  CompanyOwnedOrLed: "A company they own or lead",
  InvestmentGroupOrFund: "An investment group or fund they represent",
};
const EQUITY_SOURCE_LABEL: Record<string, string> = {
  CompanyFundsOnHand: "Company funds on hand",
  CommittedByInvestors: "Committed by investors or partners",
  StillBeingRaised: "Still being raised — subject to final capitalization",
};
const OPERATOR_ARRANGEMENT_LABEL: Record<string, string> = {
  CompanyPrincipal: "A principal of the acquiring company",
  HiredGeneralManager: "A general manager to be hired",
  ExistingManagement: "Existing management will stay",
  DesignatedPerson: "Another person designated by the buyer",
};

/** Mirror of `BuyerAcquirer.IsCompanyBuyer`: a company TYPE and a non-blank
 *  company name. A company type with no name is an individual (ruling Q11). */
function isCompanyBuyerAnswers(map: Map<string, unknown>): boolean {
  const type = map.get("buyertype");
  const name = map.get("acquiringcompanyname");
  return (
    (type === "CompanyOwnedOrLed" || type === "InvestmentGroupOrFund") &&
    typeof name === "string" &&
    name.trim() !== ""
  );
}

// The CANONICAL fit-check keys — the FE mirror of PrequalKnockoutKeys, which
// is what the engine's fixed loop reads.
//
// Enumerated here rather than iterated off the stored answers dictionary. The
// funnel writes companion `{key}__primary` and `{key}__followup` entries beside
// each canonical key to preserve branch state across a session restore, so
// walking the dictionary rendered THREE rows per question — and the companions
// carry the raw control value, which for the excluded-industry questions is
// polarity-INVERTED against the canonical one (its primary asks whether the
// business IS in an excluded industry). A favorable answer was therefore also
// being badged adverse. The engine was never affected: its lookup is exact-key,
// so `…__primary` can never match a canonical key.
//
// Declared locally rather than imported from the funnel's content module for
// the same reason as the label maps above.
//
// Two engine reasons are SYNTHESIZED and are deliberately absent: `buyer-
// absentee` (retired as a question — the active-management question replaced
// it, and PrequalKnockoutKeys.Buyer excludes it while the engine still emits
// the reason) and `seller-no-cash-flow` (never a key at all).
const BUYER_KNOCKOUT_KEYS = [
  "buyer-bankruptcy",
  "buyer-ofac",
  "buyer-felony",
  "buyer-other-debt",
  "buyer-no-guarantee",
  "buyer-excluded-industry",
  "buyer-phoenix-default",
];

const SELLER_KNOCKOUT_KEYS = [
  "seller-no-authority",
  "seller-surviving-debt",
  "seller-bankruptcy",
  "seller-excluded-industry",
  "seller-nontransferable-license",
  "seller-tax-lien",
  "seller-investigation",
  "seller-not-going-concern",
  "seller-franchisor-denied",
];

// Short column labels, authored here — no existing source has the right shape
// (`KnockoutQuestionDef.statement` is the full question prose and
// PrequalKnockoutRiskDescriptions describes the unfavorable fact).
//
// Each is phrased so the CANONICAL polarity reads true: since prequal-v2 the
// stored value is "yes" = favorable, so a label naming the RISK ("Excluded
// industry") would render "Yes" + a favorable badge for a clean answer —
// reintroducing the self-contradiction this panel is being fixed for. Each
// label therefore names the CLEARED STATE, matching the question as asked.
const KNOCKOUT_LABEL: Record<string, string> = {
  "buyer-bankruptcy": "Clear of personal bankruptcy",
  "buyer-ofac": "Clear of OFAC sanctions",
  "buyer-felony": "Clear of financial felonies (10 yrs)",
  "buyer-other-debt": "Only acquisition financing",
  "buyer-no-guarantee": "Every owner of 20% or more will personally guarantee",
  "buyer-excluded-industry": "Target outside excluded industries",
  "buyer-phoenix-default": "Phoenix obligations in good standing",
  "seller-no-authority": "Clear authority to sell",
  "seller-surviving-debt": "Phoenix note is the only financing",
  "seller-bankruptcy": "Clear of business bankruptcy",
  "seller-excluded-industry": "Outside excluded industries",
  "seller-nontransferable-license": "Licenses transfer to a buyer",
  "seller-tax-lien": "Clear of unresolvable tax liens",
  "seller-investigation": "Clear of regulatory actions",
  "seller-not-going-concern": "Operating as a going concern",
  "seller-franchisor-denied": "Franchisor consent not denied",
};

/** Mirrors `PrequalInternalRelationship`, whose members are these keys. The
 *  Letter renders the same three facts as PROSE inside a sentence the buyer is
 *  representing ("a family member of the owner of the business they are
 *  buying"); here they are column values a reviewer scans, so they are short.
 *  Two renderings of one fact ON PURPOSE — do not point one at the other. */
const INTERNAL_RELATIONSHIP_LABEL: Record<string, string> = {
  FamilyMember: "Family member of the owner",
  EmployeeOrManager: "Employee or manager",
  PartnerOrCoOwner: "Partner or co-owner",
};

const KNOCKOUT_ANSWER_LABEL: Record<string, string> = {
  yes: "Yes",
  no: "No",
  unsure: "Unsure",
  "not-a-franchise": "Not a franchise",
};

function knockoutRows(
  map: Map<string, unknown>,
  role: PrequalificationRole,
  engineVersion?: string,
): AnswerRow[] {
  const knockouts = toLowerKeyMap(map.get("knockouts"));
  const adverse = isYesFavorableEngine(engineVersion) ? "no" : "yes";
  const keys = role === "Buyer" ? BUYER_KNOCKOUT_KEYS : SELLER_KNOCKOUT_KEYS;
  return keys.map((key) => {
    const raw = knockouts.get(key);
    const answer = typeof raw === "string" ? raw.toLowerCase() : "";
    const answerLabel = labelFor(KNOCKOUT_ANSWER_LABEL, answer);
    return {
      label: labelFor(KNOCKOUT_LABEL, key) ?? humanize(key.replace(/^(buyer|seller)-/, "")),
      value: answerLabel ?? "—",
      // Unanswered — or a value we do not recognise — gets NO badge. Neither is
      // a favorable answer, and gold on "—" reads as one nobody gave.
      badge:
        answerLabel === undefined
          ? undefined
          : answer === adverse
            ? "error"
            : answer === "unsure"
              ? "review"
              : "gold",
    };
  });
}

export function buildAnswerGroups(
  role: PrequalificationRole,
  answersJson: string,
  engineVersion?: string,
  /** Which package the buyer asked for. TRAILING and OPTIONAL so the three
   *  existing callers keep compiling, and absent reads as "not the internal
   *  path" — the safe direction, since the only thing it adds is a group.
   *
   *  🔴 **KEYED ON THE PATH, NEVER ON THE ANSWERS.** Deriving internal-ness
   *  from a populated `internalRelationship` would show the group for a buyer
   *  who answered the internal step, changed their mind and switched arms: the
   *  funnel HIDES those fields and RETAINS their values, and only the payload
   *  gate clears them. The path is the authority everywhere else in this
   *  feature and is the authority here. */
  buyerDocumentPath?: PrequalBuyerDocumentPath | null,
  /** The seller's recorded Step 1 · Path (U5b). TRAILING and OPTIONAL; absent
   *  reads as a sale. Keyed on the PATH, never on the answers — the same rule as
   *  the buyer path above. */
  sellerPath?: PrequalSellerPath | null,
): AnswerGroup[] {
  let raw: unknown;
  try {
    raw = JSON.parse(answersJson);
  } catch {
    return [];
  }
  const map = toLowerKeyMap(raw);

  if (role === "Buyer") {
    const internal = buyerDocumentPath === "InternalBuyer";
    const relationship = map.get("internalrelationship");
    // Company & sponsor buyers. The group appears only once "Who is buying?"
    // was answered — every submission before the question existed renders
    // exactly as it did — and its company rows only for a company buyer.
    const buyerTypeAnswered = typeof map.get("buyertype") === "string";
    const company = isCompanyBuyerAnswers(map);
    // A company buyer who is not the operator, or (FT) an individual overseer:
    // anyone but the operator with an arrangement on the stored answers.
    const operatorAnswered =
      map.get("involvement") !== "operator" && typeof map.get("operatorarrangement") === "string";
    return [
      {
        title: "Target",
        rows: [
          // The SPECIFIC business first, then the category — the same order the
          // seller group uses, and the order a staff member reads in. Empty for
          // a buyer who is still shopping a category, and for every submission
          // finalized before the question existed.
          { label: "Business in mind", value: text(map, "targetBusinessName") },
          { label: "Target industry", value: text(map, "targetIndustry") },
          { label: "Price band", value: choice(map, "priceBand", PRICE_BAND_LABEL) },
        ],
      },
      ...(buyerTypeAnswered
        ? [{
            title: "Who is buying",
            rows: [
              { label: "Buyer type", value: choice(map, "buyerType", BUYER_TYPE_LABEL) },
              ...(company
                ? [
                    { label: "Acquiring company", value: verbatim(map, "acquiringCompanyName") },
                    { label: "Title at the company", value: verbatim(map, "acquiringCompanyTitle") },
                    { label: "Company website", value: verbatim(map, "acquiringCompanyWebsite") },
                    { label: "About the company", value: verbatim(map, "acquiringCompanyAbout") },
                  ]
                : []),
            ],
          }]
        : []),
      // 🔴 THE GROUP A REVIEWER VERIFIES AGAINST. The Relationship control in
      // the rail asks staff to confirm an assertion; without this group the
      // page never showed them what was asserted, which is a control with
      // nothing behind it. Placed immediately after the business it is about.
      ...(internal
        ? [{
            title: "Internal purchase",
            rows: [
              {
                label: "Relationship to the business",
                value: choice(map, "internalRelationship", INTERNAL_RELATIONSHIP_LABEL),
              },
              // Asked of an employee or manager and of nobody else, so the row
              // is PRESENT only for them. A dash here would read as "they did
              // not answer" on a buyer who was never asked — the one reading
              // the registry's own never-omit-a-row rule exists to prevent, and
              // the rule cannot help when the question itself is conditional.
              // Zero renders as "0": somebody who started this year, which is a
              // real answer and never a gate.
              ...(relationship === "EmployeeOrManager"
                ? [{
                    label: "Years with the business",
                    value: text(map, "internalYearsWithBusiness"),
                  }]
                : []),
              // A COUNT and nothing else — no names, no contact details. It
              // prints on no document.
              { label: "Other buyers alongside", value: text(map, "internalCoBuyerCount") },
            ],
          }]
        : []),
      {
        title: "Capital",
        rows: [
          // A company buyer's one capital answer is the acquiring company's
          // expected equity contribution (D3), with where it comes from.
          ...(company
            ? [
                { label: "Expected equity contribution", value: money(map, "cashAvailable") },
                { label: "Equity source", value: choice(map, "equitySource", EQUITY_SOURCE_LABEL) },
              ]
            : [{ label: "Cash available", value: money(map, "cashAvailable") }]),
          // Empty for a buyer who named no other assets — which is every
          // submission finalized before the section existed.
          ...cashEquivalentRows(map),
          // Amount first, then the band that translates it into months — the
          // order the funnel asks them in. The amount sizes NOTHING; the band is
          // the factor ladder's input.
          { label: "Working capital after closing", value: money(map, "reservesAmount") },
          { label: "Reserves band", value: choice(map, "reservesBand", RESERVES_BAND_LABEL) },
        ],
      },
      {
        title: "Experience & fit",
        rows: [
          {
            label: "Ownership experience",
            value: choice(map, "ownershipExperience", OWNERSHIP_LABEL),
          },
          {
            label: "Management experience",
            value: choice(map, "managementExperience", MANAGEMENT_LABEL),
          },
          { label: "Industry fit", value: choice(map, "industryFit", INDUSTRY_FIT_LABEL) },
          {
            label: "Day-to-day involvement",
            value: choice(map, "involvement", INVOLVEMENT_LABEL),
          },
          { label: "Alt-management path (AAMA)", value: boolText(map, "aamaPath") },
          // D4/FT: a buyer who is not the operator designates who will manage
          // the business full time. Present only where it was answered.
          ...(operatorAnswered
            ? [
                {
                  label: "Designated full-time operator",
                  value: choice(map, "operatorArrangement", OPERATOR_ARRANGEMENT_LABEL),
                },
                { label: "Operator's name", value: verbatim(map, "operatorName") },
              ]
            : []),
          { label: "Operating geography", value: geographyText(map) },
        ],
      },
      { title: "Fit check", rows: knockoutRows(map, role, engineVersion) },
      { title: "Goals", rows: [{ label: "Goals", value: text(map, "goals") }] },
    ];
  }

  // U5b — the Owner Recapitalization arm: a Path group first, years owned on
  // Business, no asking price or down payment on Numbers, and Plan in place of
  // Exit. The sale arm below is unchanged.
  const recap = role === "Seller" && sellerPath === "OwnerRecapitalization";
  const ownedYears = map.get("yearsownedbycurrentowners");
  return [
    ...(recap
      ? [
          {
            title: "Path",
            rows: [{ label: "Path", value: RECAP_PATH_ANSWER }],
          },
        ]
      : []),
    {
      title: "Business",
      rows: [
        { label: "Business name", value: text(map, "businessName") },
        { label: "Industry", value: text(map, "industry") },
        { label: "Location", value: text(map, "location") },
        { label: "Years in operation", value: text(map, "yearsInOperation") },
        ...(recap
          ? [
              {
                label: "Years owned and operated by the current owners",
                value: text(map, "yearsOwnedByCurrentOwners"),
                flag:
                  typeof ownedYears === "number" && ownedYears < RECAP_MINIMUM_OWNERSHIP_YEARS
                    ? "Under 3 years"
                    : undefined,
              },
            ]
          : []),
      ],
    },
    {
      title: "Numbers",
      rows: recap
        ? [
            { label: "Annual revenue", value: money(map, "annualRevenue") },
            { label: "Annual cash flow", value: money(map, "annualCashFlow") },
          ]
        : [
            { label: "Annual revenue", value: money(map, "annualRevenue") },
            { label: "Annual cash flow", value: money(map, "annualCashFlow") },
            { label: "Asking price", value: money(map, "askingPrice") },
            { label: "Buyer down payment asked", value: pctBand(map, "downPaymentBand") },
          ],
    },
    {
      title: "Owner reliance",
      rows: [
        { label: "Owner hours band", value: text(map, "ownerHoursBand") },
        { label: "30-day absence outcome", value: text(map, "absenceOutcome") },
        { label: "Revenue personally driven", value: text(map, "revenuePersonallyDrivenBand") },
        { label: "Customer concentration", value: text(map, "customerConcentration") },
      ],
    },
    {
      title: "Records",
      rows: [
        { label: "Books quality", value: text(map, "booksQuality") },
        { label: "Recent revenue spike", value: text(map, "revenueSpike") },
      ],
    },
    recap
      ? {
          title: "Plan",
          rows: [
            { label: "Who runs the business after closing", value: recapManagementLabel(map) },
            { label: "What the owners want to achieve", value: verbatim(map, "recapGoal") },
          ],
        }
      : {
          title: "Exit",
          rows: [
            { label: "Transition willingness", value: text(map, "transitionWillingness") },
            { label: "Reason for selling", value: text(map, "reasonForSelling") },
          ],
        },
    { title: "Fit check", rows: knockoutRows(map, role, engineVersion) },
  ];
}

/** U5b — the recap Path answer, in the funnel's own card label. */
const RECAP_PATH_ANSWER = "Stay and recapitalize — Owner Recapitalization Loan";

/** Mirror of the funnel's three-year rule (PrequalSellerRecap.MinimumOwnershipYears). */
const RECAP_MINIMUM_OWNERSHIP_YEARS = 3;

/** The Plan step's stored token → the funnel's own wording (F2g). */
const RECAP_MANAGEMENT_LABELS: Record<string, string> = {
  "current-roles": "The current owners, in their current roles",
  "roles-changing": "The current owners, with some roles changing",
  "designated-operator": "A designated operator, overseen by the owners",
};

function recapManagementLabel(map: Map<string, unknown>): string {
  const v = map.get("recapmanagement");
  return typeof v === "string" && v !== "" ? (RECAP_MANAGEMENT_LABELS[v] ?? humanize(v)) : "—";
}

/** U5b — the Convert to Seller helper on a recap row. */
const RECAP_CONVERT_HELPER =
  "Creates the existing company as the Seller party. A deal with this seller is set to Owner Recapitalization until it enters Pre-Approval.";

function parseStringArray(json?: string | null): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/** A buyer's supportable BAND, or null when the server did not recompute one
 *  (an older row, or a recompute that failed) — in which case the single
 *  headline figure stands. Buyers were shown one figure while sellers got a
 *  range, even though the band is what the funnel and the letter both quote.
 *
 *  Deliberately resolved here rather than inside `amountCell`: that helper is
 *  SHARED with the list page, whose row shape carries no band fields. */
function buyerBand(detail: PrequalificationDetailDto): string | null {
  const { buyingPowerLow: low, buyingPowerHigh: high } = detail;
  if (detail.role !== "Buyer" || low == null || high == null) return null;
  return `${formatCurrency(low)} – ${formatCurrency(high)}`;
}

type ReadinessFlag = { key?: string; label?: string; level?: string };

function parseReadinessFlags(json?: string | null): ReadinessFlag[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((f) => toLowerKeyMap(f)).map((m) => ({
      key: typeof m.get("key") === "string" ? (m.get("key") as string) : undefined,
      label: typeof m.get("label") === "string" ? (m.get("label") as string) : undefined,
      level: typeof m.get("level") === "string" ? (m.get("level") as string) : undefined,
    }));
  } catch {
    return [];
  }
}

// --- page -----------------------------------------------------------------

export function PrequalificationDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dialogs = useDialogs();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Prequalification.Manage");
  const canConvert = hasPermission("Prequalification.Convert");
  const { date } = useDateFormatter();

  const detailQuery = usePrequalificationDetail(id);
  const detail = detailQuery.data;
  const activityQuery = usePrequalificationActivity(id);

  // Parent is Partners — the sidebar group Prequalification actually lives in.
  // It is a group, not a route, so the crumb carries no href and renders as
  // plain trail text. The Prequalification href stays exactly as it is:
  // backend notification deep links point at it.
  useBreadcrumbs(
    detail
      ? [
          { label: "Partners" },
          { label: "Prequalification", href: "/deal-flow/prequalification" },
          { label: `${detail.firstName} ${detail.lastName}`.trim() || "Submission" },
        ]
      : null,
  );

  const assignableUsers = useAssignableUsers();
  const resolveUserName = (userId?: string | null): string =>
    (userId && assignableUsers.data?.find((u) => u.id === userId)?.displayName)
      || (userId ? userId.slice(0, 8) : "—");

  const [convertOpen, setConvertOpen] = useState(false);
  const [buyerCrmOpen, setBuyerCrmOpen] = useState(false);
  // Buyer role only: a seller is never sent to the Buyer CRM (L2), so the read
  // is not issued and neither the button nor the link-back renders. Every gate
  // — eligibility, the permission pair, the link-back's name — is the SERVER's
  // answer; nothing here re-derives one (L7, Z1, Z5).
  const buyerCrm = usePrequalificationBuyerCrm(id, detail?.role === "Buyer");
  const buyerCrmState = buyerCrm.data;
  const [docPendingId, setDocPendingId] = useState<string | null>(null);
  const [resendOpen, setResendOpen] = useState(false);
  /** Whether this outcome produces a document set at all — the one condition
   *  both recovery controls share. A third outcome would produce neither, and
   *  the server refuses it by name rather than on a default, so naming the two
   *  that DO here keeps the client from silently offering a button the server
   *  will reject. */
  // Optional-chained: this sits above the loading early-return, where `detail`
  // is still undefined. It is only read inside the loaded branch.
  const hasDocumentSet =
    detail?.outcome === "Pass" || detail?.outcome === "SoftDecline";
  /** What the last resend actually carried, kept on screen after the modal
   *  closes. The file names, not a count: "sent" without saying what went is
   *  the same non-answer the DOCUMENTS column gave before any of this. */
  const [lastResend, setLastResend] = useState<string[] | null>(null);

  const assign = useAssignPrequalification(id);
  const changeStatus = useChangePrequalificationStatus(id);
  const archive = useArchivePrequalification(id);
  const unarchive = useUnarchivePrequalification(id);
  const regenerate = useRegeneratePrequalificationDocuments(id);
  const setVerification = useSetInternalVerification(id);

  // The last run's outcome, kept on screen after the mutation settles. It is
  // the only place a term divergence is ever reported: the documents carry the
  // ORIGINAL figures by design, so the fact that the program has moved since is
  // not visible in them and has nowhere else to appear.
  const [lastRegeneration, setLastRegeneration] =
    useState<PrequalificationRegenerationResultDto | null>(null);

  const onMutationError = (title: string) => (e: Error) =>
    void dialogs.alert({ title, tone: "error", message: e.message });

  const answerGroups = useMemo(
    () =>
      detail
        ? buildAnswerGroups(
            detail.role,
            detail.answersJson,
            detail.engineVersion,
            detail.buyerDocumentPath,
            detail.sellerPath,
          )
        : [],
    [detail],
  );

  const openDocument = async (docId: string, download = false) => {
    setDocPendingId(docId);
    try {
      const link = await prequalificationApi.documentLink(id, docId);
      if (download) {
        const a = document.createElement("a");
        a.href = link.url;
        a.download = "";
        a.rel = "noopener";
        a.click();
      } else {
        window.open(link.url, "_blank", "noopener");
      }
    } catch (e) {
      void dialogs.alert({
        title: "Document unavailable",
        tone: "error",
        message: e instanceof Error ? e.message : "Could not fetch a document link.",
      });
    } finally {
      setDocPendingId(null);
    }
  };

  /** The editable Word copy, converted server-side from the exact HTML the
   *  PDF was rendered from. */
  const downloadWord = async (docId: string, docType: PrequalificationDocType) => {
    setDocPendingId(docId);
    try {
      const blob = await prequalificationApi.documentWord(id, docId);
      const reference = detail?.referenceNumber?.trim();
      downloadBlob(blob, `${DOC_LABEL[docType]}${reference ? ` ${reference}` : ""}.docx`);
    } catch (e) {
      void dialogs.alert({
        title: "Word copy unavailable",
        tone: "error",
        message: e instanceof Error ? e.message : "Could not download the Word copy.",
      });
    } finally {
      setDocPendingId(null);
    }
  };

  if (detailQuery.isLoading) {
    return <div className="text-small text-gray-mid p-space-4">Loading submission…</div>;
  }
  if (detailQuery.isError || !detail) {
    return (
      <div className="text-small text-error p-space-4">
        {(detailQuery.error as Error | undefined)?.message ?? "Submission not found."}
      </div>
    );
  }

  const transitions = STATUS_TRANSITIONS[detail.status] ?? [];
  const band = buyerBand(detail);
  const partyType = partyTypeForRole(detail.role);
  // U5b — an Owner Recapitalization: a SELLER whose recorded path is the recap
  // path (null is a pre-fork seller, a sale).
  const recapRow = detail.role === "Seller" && detail.sellerPath === "OwnerRecapitalization";

  return (
    <>
      <PageHeader
        eyebrow="Deal Flow · Prequalification"
        title={`${detail.firstName} ${detail.lastName}`.trim() || "Submission"}
        actions={
          <div className="flex flex-wrap items-center gap-space-2">
            {canManage && transitions.map((target) => (
              <Button
                key={target}
                variant="secondary"
                size="sm"
                disabled={changeStatus.isPending}
                onClick={() =>
                  changeStatus.mutate(
                    { status: target },
                    { onError: onMutationError("Status change failed") },
                  )
                }
              >
                Mark {target}
              </Button>
            ))}
            {/* Archive/Unarchive shrink to icons — they are reversible, they
                have a settled glyph, and only one of the pair is ever on screen,
                so there is no second icon to confuse this one with. The word it
                used to spell out is still the accessible name, and the Tooltip
                repeats it on hover and on keyboard focus.

                The two status buttons above and Convert below deliberately KEEP
                their text: "Mark Reviewed" and "Mark Disqualified" have no
                distinguishing glyph between them, and hiding the one that
                disqualifies a lead behind a hover is worse than the crowding
                this is fixing. Convert is a one-way door. */}
            {canManage && !detail.isArchived && detail.status !== "Converted" && (
              <Tooltip label="Archive">
                <IconBtn
                  label="Archive"
                  className="disabled:opacity-40"
                  disabled={archive.isPending}
                  onClick={() => archive.mutate(undefined, { onError: onMutationError("Archive failed") })}
                >
                  <Archive size={16} strokeWidth={1.5} aria-hidden />
                </IconBtn>
              </Tooltip>
            )}
            {canManage && detail.isArchived && (
              <Tooltip label="Unarchive">
                <IconBtn
                  label="Unarchive"
                  className="disabled:opacity-40"
                  disabled={unarchive.isPending}
                  onClick={() => unarchive.mutate(undefined, { onError: onMutationError("Unarchive failed") })}
                >
                  <ArchiveRestore size={16} strokeWidth={1.5} aria-hidden />
                </IconBtn>
              </Tooltip>
            )}
            {/* Gated on BOTH pointers. A legacy row carries only the Person one,
                and offering Convert on it would put a second identity record
                behind a submission that already has one — the server refuses on
                either pointer, so the button would only ever produce an error. */}
            {canConvert && !detail.convertedToPersonId && !detail.convertedToPartyId && (
              recapRow ? (
                // U5b: the label is unchanged; a recap row says what converting
                // it sets in motion (the deal-type auto-set, surface U4).
                <Tooltip label={RECAP_CONVERT_HELPER}>
                  <Button size="sm" onClick={() => setConvertOpen(true)}>
                    <UserCheck2 size={14} strokeWidth={1.5} aria-hidden /> Convert to {partyType}
                  </Button>
                </Tooltip>
              ) : (
                <Button size="sm" onClick={() => setConvertOpen(true)}>
                  <UserCheck2 size={14} strokeWidth={1.5} aria-hidden /> Convert to {partyType}
                </Button>
              )
            )}
            {buyerCrmState?.eligible && buyerCrmState.canSend && (
              <Button size="sm" variant="secondary" onClick={() => setBuyerCrmOpen(true)}>
                <Inbox size={14} strokeWidth={1.5} aria-hidden /> Send to assignment queue
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-space-2 mb-space-4">
        {/* The human-quotable id, at the top of the page because quoting it on
            a call is its whole job. Until now the only reference number on this
            page was the PREVIOUS submission's, on the chain link below. */}
        {detail.referenceNumber && (
          <span
            className="font-mono text-caption text-onyx bg-white-warm border border-border-light rounded-md px-space-2 py-space-1"
            data-testid="prequal-reference-number"
          >
            Ref {detail.referenceNumber}
          </span>
        )}
        <Badge status={detail.role === "Buyer" ? "info" : "gold"}>{detail.role}</Badge>
        {/* U5b: the recap path, beside the role (dark `draft` variant, as on the list). */}
        {recapRow && <Badge status="draft">Recapitalization</Badge>}
        <Badge status={PREQUAL_STATUS_BADGE[detail.status]}>{detail.status}</Badge>
        <Badge status={PREQUAL_OUTCOME_BADGE[detail.outcome]}>{outcomeLabel(detail.outcome)}</Badge>
        {detail.isArchived && <Badge status="archived">Archived</Badge>}
        {/* Whichever pointer is set — a converted submission has exactly one.
            The legacy Person link is kept VERBATIM rather than re-pointed: those
            rows are in a different table, that route is still registered, and
            re-pointing them at /borrowers would deep-link to nothing. */}
        {detail.convertedToPartyId && (
          <Link
            to={`${partyBasePath(partyType)}/${detail.convertedToPartyId}`}
            className="text-caption text-gold-shadow hover:underline inline-flex items-center gap-space-1"
          >
            View {partyType} record <ExternalLink size={12} strokeWidth={1.5} aria-hidden />
          </Link>
        )}
        {detail.convertedToPersonId && (
          <Link
            to={`/counterparties/persons/${detail.convertedToPersonId}`}
            className="text-caption text-gold-shadow hover:underline inline-flex items-center gap-space-1"
          >
            View Person record <ExternalLink size={12} strokeWidth={1.5} aria-hidden />
          </Link>
        )}
        {buyerCrmState?.linkedBuyer && <BuyerCrmLinkBack linked={buyerCrmState.linkedBuyer} />}
      </div>

      <ResponsiveSplit
        gap="md"
        mainMinWidth="34rem"
        railWidth={RECORD_RAIL_WIDTH}
        railGrows={false}
        main={
          <div className="flex flex-col gap-space-4">
            <Panel title="Computed result">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-space-4 gap-y-space-2">
                {recapRow && <Field label="Seller path" value="Owner Recapitalization" />}
                <Field label="Outcome" value={outcomeLabel(detail.outcome)} />
                {/* 🔴 D5 — "Buying power" is a claim about the buyer's WALLET,
                    and on an internal purchase this band is a price range sized
                    from the business's own free cash flow. The figure is right;
                    the label described the wrong thing about it.

                    Named the same as the Letter's At-a-Glance row and the result
                    screen's hero, deliberately: three surfaces, one figure, one
                    name — a staff member on the phone to an applicant is reading
                    the third of them. Not conditioned on `band` the way the
                    capital arm is, because the figure is the same kind of figure
                    with or without a range to show for it. */}
                {detail.schedules && detail.schedules.length > 0 ? (
                  // prequal-v7 (M41, D2): the two schedules side by side, with the
                  // staff-only Origination Coverage Ratio and the D2 flags. A row
                  // sized before v7 carries no schedules and keeps the single
                  // field below, exactly as it read (D15).
                  <ScheduleFields schedules={detail.schedules} recap={recapRow} />
                ) : (
                  <Field
                    label={
                      detail.role !== "Buyer"
                        ? "Supportable range"
                        : detail.buyerDocumentPath === "InternalBuyer"
                          ? "Qualified purchase price"
                          : band
                            ? "Buying power"
                            : "Qualified amount"
                    }
                    value={band ?? amountCell(detail)}
                  />
                )}
                <Field label="Engine version" value={detail.engineVersion || "—"} />
                <Field label="Submitted" value={date(detail.createdAt)} />
                {/* How long the stated figures stand — dropped from this page the
                    same way the reference number was. */}
                <Field
                  label="Valid through"
                  value={detail.validThroughUtc ? date(detail.validThroughUtc) : "—"}
                />
              </dl>
              {/* Ruling Q4: a prequalification a Buyer Consultant took on a
                  telephone call says so, and who certified it. Nothing renders
                  for any other completion mode. The date is an INSTANT. */}
              {(() => {
                const line = phoneCertificationLine(detail, date);
                return line ? (
                  <div
                    className="mt-space-2 text-caption text-gray-mid"
                    data-testid="prequal-phone-certification"
                  >
                    {line}
                  </div>
                ) : null;
              })()}
              {detail.partnerSlug && (
                <div className="mt-space-2 text-caption text-gray-mid">
                  Sourced via {detail.partnerSlug}
                </div>
              )}
              {detail.previousSubmission && (
                // A repeat buyer. The earlier submission is NOT superseded — it
                // stays live and stays material, which is why the outcome is on
                // the face of the link rather than behind it: a January decline
                // that underwriting has to click to discover is one nobody
                // discovers.
                <div
                  className="mt-space-3 rounded-md border border-border-light bg-white-warm/40 p-space-3"
                  data-testid="prequal-previous-submission"
                >
                  <div className="text-caption text-gray-mid">
                    This buyer prequalified here before
                  </div>
                  <div className="mt-space-1 text-small text-gray-dark">
                    <Link
                      to={`/deal-flow/prequalification/${detail.previousSubmission.id}`}
                      className="text-gold-shadow hover:underline"
                    >
                      {detail.previousSubmission.referenceNumber ?? "Earlier submission"}
                    </Link>
                    {" — "}
                    {outcomeLabel(detail.previousSubmission.outcome)}
                    {" on "}
                    {date(detail.previousSubmission.createdAt)}
                  </div>
                </div>
              )}
              <ReasonList
                title="Knockout reasons"
                badge="error"
                items={parseStringArray(detail.knockoutReasonsJson).map(humanize)}
              />
              <ReasonList
                title="Answered “Unsure” — follow up"
                badge="review"
                items={parseStringArray(detail.unsureFlagsJson).map(humanize)}
              />
              {parseReadinessFlags(detail.readinessFlagsJson).length > 0 && (
                <div className="mt-space-3">
                  <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">Readiness flags</div>
                  <ul className="flex flex-col gap-space-1">
                    {parseReadinessFlags(detail.readinessFlagsJson).map((f, i) => (
                      <li key={f.key ?? i} className="flex items-center gap-space-2 text-small">
                        <Badge status={f.level === "watch" ? "review" : "gold"}>
                          {f.level === "watch" ? "Watch" : "OK"}
                        </Badge>
                        {f.label ?? humanize(f.key ?? "")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Panel>

            {answerGroups.map((group) => (
              <Panel key={group.title} title={group.title}>
                {group.rows.length === 0 ? (
                  <div className="text-small text-gray-mid italic">No answers recorded.</div>
                ) : (
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-space-4 gap-y-space-2">
                    {group.rows.map((row) => (
                      <Field
                        flag={row.flag}
                        key={row.label}
                        label={row.label}
                        value={row.value}
                        badge={row.badge}
                      />
                    ))}
                  </dl>
                )}
              </Panel>
            ))}
        </div>
        }
        rail={
          <div className="grid gap-space-4 min-w-0">

          <div className="flex flex-col gap-space-4">
            <Panel title="Contact">
              <dl className="flex flex-col gap-space-2">
                <Field label="Email" value={detail.email} />
                {/* Display only — the stored value is untouched. `formatPhone`
                    returns "" for a blank and echoes anything it cannot parse. */}
                <Field label="Phone" value={formatPhone(detail.phone) || "—"} />
                <Field label="Consent accepted" value={date(detail.consentAcceptedAtUtc)} />
                {detail.purgeEligibleAtUtc && (
                  <Field label="Purge eligible" value={date(detail.purgeEligibleAtUtc)} />
                )}
              </dl>
            </Panel>

            <Panel title="Assigned staff">
              {canManage ? (
                <select
                  className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small"
                  value={detail.assignedToUserId ?? ""}
                  aria-label="Assigned staff"
                  disabled={assign.isPending}
                  onChange={(e) =>
                    assign.mutate(e.target.value || null, {
                      onError: onMutationError("Reassign failed"),
                    })
                  }
                >
                  <option value="">Unassigned</option>
                  {(assignableUsers.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>{u.displayName}</option>
                  ))}
                </select>
              ) : (
                <div className="text-small">{resolveUserName(detail.assignedToUserId)}</div>
              )}
            </Panel>

            {/* 🔴 KEYED ON THE PATH, and rendered for nobody else. The column
                behind this is NOT NULL on every submission and defaults to
                Unverified, so a panel keyed on the value alone would ask every
                seller in the book to verify a relationship nobody asserted.
                The answers group in the main column is what a reviewer reads
                before answering this. */}
            {detail.buyerDocumentPath === "InternalBuyer" && (
              <Panel title="Relationship">
                {canManage ? (
                  <select
                    className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small"
                    value={detail.internalRelationshipVerification}
                    aria-label="Relationship verification"
                    disabled={setVerification.isPending}
                    onChange={(e) =>
                      setVerification.mutate(
                        e.target.value as PrequalInternalVerification,
                        { onError: onMutationError("Could not record that") },
                      )
                    }
                  >
                    {/* The enum members are near-homographs; these labels are
                        not. Unchecked = nobody has looked, which is the default
                        and not a problem. Not verified = a person looked and it
                        did not hold. */}
                    <option value="Unverified">Unchecked</option>
                    <option value="Verified">Verified</option>
                    <option value="NotVerified">Not verified</option>
                  </select>
                ) : (
                  (() => {
                    const badge = internalVerificationBadge(detail);
                    return badge
                      ? <Badge status={badge.status}>{badge.label}</Badge>
                      : null;
                  })()
                )}
                {/* Re-asserting the SAME value is not a no-op server-side, on
                    purpose: it re-stamps who and when, which is what answers
                    "how recently did anyone check this?" — the question the
                    stamp exists for. */}
                {detail.internalRelationshipVerifiedAtUtc && (
                  <div className="text-caption text-gray-mid mt-space-2">
                    {resolveUserName(detail.internalRelationshipVerifiedByUserId)}
                    {" · "}
                    {date(detail.internalRelationshipVerifiedAtUtc)}
                  </div>
                )}
                {/* D6, said on the surface rather than only in the code. A red
                    badge in this product reads as blocking, and this one is
                    not: nothing downstream reads this value. Stating it here is
                    what stops a reviewer treating "Not verified" as a stop. */}
                <p className="text-caption text-gray-mid mt-space-2">
                  Recorded for the file. It does not gate documents, emails or
                  any later step.
                </p>
              </Panel>
            )}

            <Panel title="Documents">
              {/* 🔴 Why the set is the size it is, above the set itself. A
                  letter-only buyer's Deal Sheet is absent BY REQUEST, and a
                  panel that lists only what exists cannot tell that apart from a
                  generation that failed — which is the reading that let the
                  eleven-day total-loss incident look like a soft decline. Only
                  the letter-only case says anything: "wanted both" is what every
                  reader already assumes, and a row restating it on every other
                  submission is noise. */}
              {detail.buyerDocumentPath === "LetterOnly" && (
                <div className="text-small text-gray-mid mb-space-3">
                  This buyer asked for proof of buying power only — no Deal Sheet
                  was prepared.
                </div>
              )}
              {detail.documents.length === 0 ? (
                <div className="text-small text-gray-mid italic mb-space-3">
                  No documents generated.
                </div>
              ) : (
                <ul className="flex flex-col gap-space-2 mb-space-3">
                  {detail.documents.map((doc) => (
                    <li key={doc.id} className="flex items-center justify-between gap-space-2">
                      <div>
                        <div className="text-small font-semibold">
                          {DOC_LABEL[doc.docType]}
                          {/* Marked, never hidden. A regenerate is append-only
                              because the old document is the record of what the
                              applicant was actually sent, so a staff surface
                              that dropped it would be dropping the evidence —
                              what staff need is to tell them apart. */}
                          {doc.isSuperseded && (
                            <span className="ml-space-2 text-caption font-normal text-gray-mid">
                              superseded
                            </span>
                          )}
                        </div>
                        <div className="text-caption text-gray-mid">{date(doc.generatedAtUtc)}</div>
                      </div>
                      <div className="flex gap-space-1">
                        <Button
                          variant="tertiary"
                          size="sm"
                          disabled={docPendingId === doc.id}
                          onClick={() => void openDocument(doc.id)}
                          aria-label={`View ${DOC_LABEL[doc.docType]}`}
                        >
                          <ExternalLink size={14} strokeWidth={1.5} aria-hidden /> View
                        </Button>
                        <Button
                          variant="tertiary"
                          size="sm"
                          disabled={docPendingId === doc.id}
                          onClick={() => void openDocument(doc.id, true)}
                          aria-label={`Download ${DOC_LABEL[doc.docType]}`}
                        >
                          <Download size={14} strokeWidth={1.5} aria-hidden />
                        </Button>
                        {doc.hasWordCopy && (
                          <Button
                            variant="tertiary"
                            size="sm"
                            disabled={docPendingId === doc.id}
                            onClick={() => void downloadWord(doc.id, doc.docType)}
                            aria-label={`Download Word copy of ${DOC_LABEL[doc.docType]}`}
                          >
                            Download Word
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {detail.documents.some((doc) => doc.hasWordCopy) && (
                <p className="text-caption text-gray-mid mb-space-3">
                  {"Edited copies are sent outside Phoenix and aren't recorded here — log the send with Log contact."}
                </p>
              )}

              {/* Offered on EVERY submission that produces documents, not only on
                  one that is visibly missing something: the other reason to press
                  this is a copy correction in the Document Language Editor, and
                  that submission looks complete.

                  🔴 A SOFT DECLINE IS INCLUDED NOW. It used to be excluded on the
                  true premise that a decline generated nothing; a decline now
                  produces a letter and an analysis, and those are exactly the
                  documents a staff member needs to be able to reproduce. */}
              {hasDocumentSet && (
                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={regenerate.isPending}
                  onClick={() =>
                    regenerate.mutate(undefined, {
                      onSuccess: (r) => setLastRegeneration(r),
                      onError: onMutationError("Regenerate failed"),
                    })
                  }
                >
                  <RefreshCw size={14} strokeWidth={1.5} aria-hidden />{" "}
                  {regenerate.isPending ? "Regenerating…" : "Regenerate documents"}
                </Button>
              )}

              {/* 🔴 A SECOND, SEPARATE control (D6), never a step folded into
                  Regenerate. The two do different things to different people:
                  one changes what the documents SAY, the other puts them in an
                  applicant's inbox. Combining them would mean every fix to a
                  typo mailed the applicant, and every resend silently re-rendered
                  a document they may already be holding.

                  Offered on the same condition as Regenerate, and a soft decline
                  now qualifies: it has its own completion email, from its own
                  composer and its own copy set. Every narrower refusal (no
                  documents, a suppressed address, an oversized set) is the
                  server's, surfaced INSIDE the modal where the reason can be
                  read, rather than hidden behind a button that does not
                  appear. */}
              {hasDocumentSet && (
                <Button
                  variant="tertiary"
                  size="sm"
                  className="mt-space-2"
                  onClick={() => setResendOpen(true)}
                  data-testid="prequal-resend-open"
                >
                  <Mail size={14} strokeWidth={1.5} aria-hidden /> Resend to applicant
                </Button>
              )}

              {lastResend && (
                <div className="mt-space-3 text-caption text-gray-mid" role="status">
                  {lastResend.length === 0
                    ? "Sent."
                    : `Sent with ${lastResend.join(", ")}.`}
                </div>
              )}

              {lastRegeneration && (
                <div className="mt-space-3 flex flex-col gap-space-2" role="status">
                  <div className="text-caption text-gray-mid">
                    {lastRegeneration.produced.length === 0
                      ? "Nothing was produced. The previous documents, if any, still stand."
                      : `Regenerated: ${lastRegeneration.produced
                          .map((k) => DOC_LABEL[k])
                          .join(", ")}.`}
                  </div>

                  {lastRegeneration.failed.length > 0 && (
                    <div className="text-caption text-status-error">
                      Could not generate:{" "}
                      {lastRegeneration.failed.map((k) => DOC_LABEL[k]).join(", ")}.
                    </div>
                  )}

                  {/* The figures were replayed, so a source of "Recomputed" is
                      the one case where a number could differ from what the
                      applicant was originally given. It is said out loud rather
                      than left to be noticed. */}
                  {lastRegeneration.figureSource === "Recomputed" && (
                    <div className="text-caption text-status-warning">
                      No stored figures were found for this submission, so they were
                      recomputed from the answers. Check the documents before sending them.
                    </div>
                  )}

                  {lastRegeneration.termDivergences.length > 0 && (
                    <div className="text-caption text-status-warning">
                      Program terms have changed since these figures were set
                      ({lastRegeneration.termDivergences.map((t) => t.name).join(", ")}). The
                      documents repeat the ORIGINAL figures on purpose — re-quoting is a
                      decision for you, not for the renderer.
                    </div>
                  )}
                </div>
              )}
            </Panel>

            <Panel title="Activity">
              <ActivityList
                loading={activityQuery.isLoading}
                items={activityQuery.data ?? []}
                resolveUserName={resolveUserName}
              />
            </Panel>
          </div>
      </div>
        }
      />

      {buyerCrmOpen && buyerCrmState && (
        <SendToBuyerCrmModal
          submissionId={id}
          state={buyerCrmState}
          onClose={() => setBuyerCrmOpen(false)}
        />
      )}
      {convertOpen && (
        <ConvertModal
          submissionId={id}
          detail={detail}
          partyType={partyType}
          onClose={() => setConvertOpen(false)}
          onConverted={(partyId) => {
            setConvertOpen(false);
            navigate(`${partyBasePath(partyType)}/${partyId}`);
          }}
        />
      )}

      {resendOpen && (
        <PrequalificationResendModal
          submissionId={id}
          onClose={() => setResendOpen(false)}
          onSent={setLastResend}
        />
      )}
    </>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border border-border-light rounded-md bg-white-pure p-space-4">
      <h2 className="font-display text-h4 text-onyx mb-space-3">{title}</h2>
      {children}
    </section>
  );
}

/** Money to the cent — a staff screen (ruling Q1). */
function moneyCents(amount: number | null | undefined): string {
  return amount == null
    ? "—"
    : formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A coverage ratio as "1.61x". */
function ratio(value: number | null | undefined): string {
  return value == null ? "—" : `${value.toFixed(2)}x`;
}

/** Which §2 T5 limit set a schedule's Maximum Financed Amount, in words. */
export function bindingLimitLabel(binding: string | null | undefined): string {
  switch (binding) {
    case "FiveTimesCashFlow":
      return `${PREQUAL_PROGRAM_TERMS.maxCashFlowMultiple}× free cash flow`;
    case "CoverageOnBuyerNotePrincipal":
      return `${PREQUAL_PROGRAM_TERMS.sizingCoverage.toFixed(2)}x coverage on the Buyer Note Principal`;
    case "ProgramMaximum":
      return `${formatCurrency(PREQUAL_PROGRAM_TERMS.maxAmountFinanced)} program maximum`;
    default:
      return "—";
  }
}

/**
 * The v7 Computed result rows (M41): each schedule's Maximum Financed Amount and
 * the limit that set it, the Buyer Note Principal and the twelve months' basis,
 * the Origination Coverage Ratio against the 1.35x floor (staff only, D2), and —
 * when the applicant named a price — the D2 flags on the amount financed it
 * implies (ruling Q11 (b)): a flag, never a block.
 */
function ScheduleFields({
  schedules,
  recap = false,
}: {
  schedules: PrequalScheduleFiguresDto[];
  /** U5b: an Owner Recapitalization row adds the seller's Underwriting Fee at the
   *  maximum on both schedules. */
  recap?: boolean;
}) {
  const s20 = schedules.find((s) => s.amortizationMonths === 240);
  const s10 = schedules.find((s) => s.amortizationMonths === 120);
  if (!s20 || !s10) return null;
  const minimum = formatCurrency(PREQUAL_PROGRAM_TERMS.minAmountFinanced);
  const floor = PREQUAL_PROGRAM_TERMS.originationCoverageFloor;
  const ocrBadge = (s: PrequalScheduleFiguresDto): "gold" | "error" =>
    s.originationCoverage != null && s.originationCoverage < floor ? "error" : "gold";
  const maxField = (s: PrequalScheduleFiguresDto, years: number) =>
    s.belowMinimum ? (
      <Field
        label={`Maximum Financed Amount — ${years}-year`}
        value={`${moneyCents(s.maximumFinancedAmount)} — Below the ${minimum} program minimum`}
        badge="error"
      />
    ) : (
      <Field label={`Maximum Financed Amount — ${years}-year`} value={moneyCents(s.maximumFinancedAmount)} />
    );
  const stated = s20.statedAmountFinanced;
  const exceeded = [s20, s10].filter((s) => s.statedExceedsMaximumFlag);
  const exceedsText =
    stated != null && exceeded.length > 0
      ? `Stated price less down payment ${moneyCents(stated)} exceeds ${exceeded
          .map(
            (s) =>
              `the ${s.amortizationMonths / 12}-year maximum (${moneyCents(s.maximumFinancedAmount)})`,
          )
          .join(" and ")} — flag, not a block`
      : null;
  const statedOcrBelow = [s20, s10].some(
    (s) => s.statedOriginationCoverage != null && s.statedOriginationCoverage < floor,
  );
  return (
    <>
      {maxField(s20, 20)}
      {maxField(s10, 10)}
      <Field label="Limit that binds — 20-year" value={bindingLimitLabel(s20.bindingLimit)} />
      <Field label="Limit that binds — 10-year" value={bindingLimitLabel(s10.bindingLimit)} />
      <Field
        label="Buyer Note Principal at the maximum"
        value={`20-yr ${moneyCents(s20.buyerNotePrincipal)} · 10-yr ${moneyCents(s10.buyerNotePrincipal)}`}
      />
      <Field
        label="Origination Coverage Ratio — 20-year"
        value={`${ratio(s20.originationCoverage)} (floor ${floor.toFixed(2)}x)`}
        badge={ocrBadge(s20)}
      />
      <Field
        label="Origination Coverage Ratio — 10-year"
        value={`${ratio(s10.originationCoverage)} (floor ${floor.toFixed(2)}x)`}
        badge={ocrBadge(s10)}
      />
      <Field
        label="Twelve months’ P&I + Payment Administration Fee"
        value={`20-yr ${moneyCents(s20.twelveMonthPrincipalAndInterest)} + ${moneyCents(
          s20.twelveMonthPaymentAdministrationFee,
        )} · 10-yr ${moneyCents(s10.twelveMonthPrincipalAndInterest)} + ${moneyCents(
          s10.twelveMonthPaymentAdministrationFee,
        )}`}
      />
      {recap && (
        <Field
          label="Underwriting Fee at the maximum"
          value={`20-yr ${moneyCents(s20.sellerUnderwritingFee)} · 10-yr ${moneyCents(s10.sellerUnderwritingFee)}`}
        />
      )}
      {exceedsText && <Field label="Over the maximum" value={exceedsText} badge="error" />}
      {stated != null && (
        <Field
          label="Origination Coverage Ratio at the stated amount"
          value={`20-yr ${ratio(s20.statedOriginationCoverage)} · 10-yr ${ratio(
            s10.statedOriginationCoverage,
          )}`}
          badge={statedOcrBelow ? "error" : "gold"}
        />
      )}
    </>
  );
}

function Field({
  label,
  value,
  badge,
  flag,
}: {
  label: string;
  value: string;
  badge?: "gold" | "review" | "error";
  /** A separate error badge beside the value. */
  flag?: string;
}) {
  return (
    <div>
      <dt className="text-caption uppercase tracking-wide text-gray-mid">{label}</dt>
      <dd className="text-small text-onyx">
        {badge ? <Badge status={badge}>{value}</Badge> : value}
        {flag && (
          <>
            {" "}
            <Badge status="error">{flag}</Badge>
          </>
        )}
      </dd>
    </div>
  );
}

function ReasonList({ title, badge, items }: { title: string; badge: "error" | "review"; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-space-3">
      <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">{title}</div>
      <ul className="flex flex-wrap gap-space-1">
        {items.map((item) => (
          <li key={item}><Badge status={badge}>{item}</Badge></li>
        ))}
      </ul>
    </div>
  );
}

function ActivityList({
  loading,
  items,
  resolveUserName,
}: {
  loading: boolean;
  items: PrequalificationActivityDto[];
  resolveUserName: (userId?: string | null) => string;
}) {
  const { dateTime } = useDateFormatter();
  if (loading) return <div className="text-small text-gray-mid">Loading activity…</div>;
  if (items.length === 0) {
    return <div className="text-small text-gray-mid italic">No activity yet.</div>;
  }
  return (
    <ul className="flex flex-col gap-space-2">
      {items.map((a) => (
        <li key={a.id} className="border-b border-border-light last:border-b-0 pb-space-2 last:pb-0">
          <div className="text-small">{a.description}</div>
          <div className="text-caption text-gray-mid">
            {a.performedByUserId === "public" ? "Public funnel" : resolveUserName(a.performedByUserId)}
            {" · "}
            {dateTime(a.timestamp)}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ConvertModal({
  submissionId,
  detail,
  partyType,
  onClose,
  onConverted,
}: {
  submissionId: string;
  detail: PrequalificationDetailDto;
  /** Derived from the submission's role by the caller — there is no picker for
   *  it here, and adding one would give the operator a way to disagree with the
   *  server's own derivation. */
  partyType: PartyType;
  onClose: () => void;
  onConverted: (partyId: string) => void;
}) {
  const convert = useConvertPrequalification(submissionId);
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [existingParty, setExistingParty] = useState<SelectedParty | null>(null);
  const [email, setEmail] = useState(detail.email);
  const [reason, setReason] = useState("");
  // Rendered inline rather than through `dialogs.alert`: the same-type refusal
  // tells the operator to link the existing record instead, and that affordance
  // is a control on this modal — the instruction belongs beside it, not behind a
  // dialog that has to be dismissed first.
  const [error, setError] = useState<string | null>(null);

  const noun = partyType.toLowerCase();
  const trimmedEmail = email.trim();
  const emailEdited =
    trimmedEmail.toLowerCase() !== detail.email.trim().toLowerCase();

  const submit = () => {
    setError(null);
    convert.mutate(
      {
        existingPartyId: mode === "existing" ? existingParty?.id ?? null : null,
        // Sent ONLY when the operator actually changed it. Blank/absent is the
        // server's documented "use the submission's own address" path, so
        // round-tripping an untouched value would record a correction that was
        // never made — and in link-existing mode the field does not apply at all.
        email: mode === "new" && emailEdited ? trimmedEmail : null,
        overrideReason: reason.trim() || null,
      },
      {
        onSuccess: (partyId) => onConverted(partyId),
        // The server's own text, verbatim. It names the holder's TYPE and, for a
        // same-type collision, points at the link-existing option above.
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Convert to ${partyType}`}
      description={`Creates (or links) a ${noun} record from this submission. Generated documents stay on the submission and remain accessible from the ${noun}'s lineage.`}
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose} disabled={convert.isPending}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={
              convert.isPending
              || (mode === "existing" ? !existingParty : !trimmedEmail)
            }
          >
            {convert.isPending ? "Converting…" : "Convert"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3">
        {detail.role === "Seller" && detail.sellerPath === "OwnerRecapitalization" && (
          <p className="text-small text-gray-mid m-0" data-testid="prequal-convert-recap-helper">
            {RECAP_CONVERT_HELPER}
          </p>
        )}
        {error && (
          <div
            role="alert"
            className="text-small text-status-error border border-status-error/40 rounded-md p-space-2"
          >
            {error}
          </div>
        )}
        <label className="flex items-start gap-space-2 text-small">
          <input
            type="radio"
            name="convert-mode"
            checked={mode === "new"}
            onChange={() => setMode("new")}
            className="mt-1"
          />
          <span>
            <span className="font-semibold">Create a new {partyType}</span>
            <span className="block text-caption text-gray-mid">
              {detail.firstName} {detail.lastName}
            </span>
          </span>
        </label>
        {mode === "new" && (
          <div>
            <Label htmlFor="convert-email">Email</Label>
            <Input
              id="convert-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-caption text-gray-mid mt-1 m-0">
              Applies to the new {noun} record only. The submission keeps the
              address the applicant entered.
            </p>
          </div>
        )}
        <label className="flex items-start gap-space-2 text-small">
          <input
            type="radio"
            name="convert-mode"
            checked={mode === "existing"}
            onChange={() => setMode("existing")}
            className="mt-1"
          />
          <span>
            <span className="font-semibold">Link an existing {partyType}</span>
            <span className="block text-caption text-gray-mid">
              Search the {noun} registry for the record this submission belongs to.
            </span>
          </span>
        </label>
        {mode === "existing" && (
          // Scoped to the DERIVED type, so cross-type linking is not reachable
          // from the UI at all — the server refuses it as a 404 regardless.
          <PartyPicker
            type={partyType}
            value={existingParty}
            onChange={setExistingParty}
            label={`Existing ${noun}`}
          />
        )}
        <div>
          <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">
            Note (optional)
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Why this conversion / linkage was made"
          />
        </div>
      </div>
    </Modal>
  );
}
